import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useUi } from '../../context/UiContext';
import { useAuth } from '../../auth/AuthContext';
import { Package, Terminal, LayoutDashboard, FileText, Search } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { getProductCatalog } from '../../api/productCatalog';
import type { Product } from '../../types';

interface CommandItem {
  label: string;
  description?: string;
  href: string;
  icon: LucideIcon;
}

export const CommandPalette: React.FC = () => {
  const { t } = useLanguage();
  const { isCommandPaletteOpen, setIsCommandPaletteOpen } = useUi();
  const { isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(!isCommandPaletteOpen);
      }
      if (e.key === 'Escape') setIsCommandPaletteOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isCommandPaletteOpen, setIsCommandPaletteOpen]);

  useEffect(() => {
    if (!isCommandPaletteOpen || products.length > 0) return;
    let cancelled = false;
    getProductCatalog()
      .then((catalog) => {
        if (!cancelled) setProducts(catalog);
      })
      .catch(() => {
        /* navigation remains available without catalog search */
      });
    return () => {
      cancelled = true;
    };
  }, [isCommandPaletteOpen, products.length]);

  const items = useMemo(() => {
    const base: CommandItem[] = [
      { label: t('command.home'), href: '/', icon: Package },
      {
        label: t('nav.applications'),
        href: '/applications',
        icon: Package,
      },
      { label: t('nav.docs'), href: '/docs', icon: FileText },
      { label: t('command.signIn'), href: '/login', icon: Terminal },
    ];
    if (isAuthenticated) {
      base.push({
        label: isAdmin ? t('command.adminPortal') : t('command.customerPortal'),
        href: isAdmin ? '/admin' : '/customer',
        icon: LayoutDashboard,
      });
    }
    const productItems: CommandItem[] = products.map((product) => ({
      label: product.name,
      description: product.tagline,
      href: `/applications/${product.slug}`,
      icon: Package,
    }));
    const query = q.trim().toLocaleLowerCase();
    return [...base, ...productItems].filter(
      (item) =>
        item.label.toLocaleLowerCase().includes(query) ||
        Boolean(item.description?.toLocaleLowerCase().includes(query))
    );
  }, [t, q, isAuthenticated, isAdmin, products]);

  if (!isCommandPaletteOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/55 backdrop-blur-[2px] flex items-start justify-center pt-[15vh] px-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setIsCommandPaletteOpen(false);
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('command.paletteAria')}
        className="relative w-full max-w-lg overflow-hidden rounded-lg border border-line dark:border-line-dark bg-white dark:bg-ink-elevated shadow-[var(--bc-shadow-md)] before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-brand-500"
      >
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-line dark:border-line-dark">
          <Search className="w-4 h-4 text-brand-500" strokeWidth={1.75} />
          <input
            autoFocus
            aria-label={t('search.placeholder')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('search.placeholder')}
            className="flex-1 bg-transparent text-sm text-ink dark:text-paper placeholder:text-fog outline-none"
          />
        </div>
        <ul className="max-h-72 overflow-auto p-2">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.href}>
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-xs font-semibold text-ink dark:text-paper hover:bg-brand-500/8 focus:outline-none focus:bg-brand-500/10 focus:ring-1 focus:ring-brand-500/25 text-left transition-colors duration-150"
                  onClick={() => {
                    setIsCommandPaletteOpen(false);
                    navigate(item.href);
                  }}
                >
                  <Icon className="w-4 h-4 text-brand-500" strokeWidth={1.75} />
                  <span className="min-w-0 text-start">
                    <span className="block truncate">{item.label}</span>
                    {item.description && (
                      <span className="block truncate text-[10px] font-normal text-fog mt-0.5">
                        {item.description}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
